import React, { useState, useEffect } from 'react';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import {

  Box,
  Typography,
  Fade,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import CustomButton from '../widgets/CustomButton';
import ResponsiveContainer from '../layouts/ResponsiveContainer';
import { font } from '../../utils/fontHelper';

interface LanguageSelectionStepProps {
  initialLanguages: string[];
  onComplete: (languages: string[]) => void;
  onSkip?: () => void; // New prop for skipping entire onboarding
  onBack?: () => void;
}

interface Language {
  code: string;
  name: string;
  nativeName: string;
}

// Match the exact language options from SettingsScreen
const AVAILABLE_LANGUAGES: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी' },
  { code: 'ko', name: 'Korean', nativeName: '한국어' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk' },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština' },
  { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina' },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română' },
  { code: 'bg', name: 'Bulgarian', nativeName: 'Български' },
  { code: 'hr', name: 'Croatian', nativeName: 'Hrvatski' },
  { code: 'sr', name: 'Serbian', nativeName: 'Српски' },
  { code: 'sl', name: 'Slovenian', nativeName: 'Slovenščina' },
  { code: 'et', name: 'Estonian', nativeName: 'Eesti' },
  { code: 'lv', name: 'Latvian', nativeName: 'Latviešu' },
  { code: 'lt', name: 'Lithuanian', nativeName: 'Lietuvių' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu' },
  { code: 'tl', name: 'Filipino', nativeName: 'Filipino' },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית' },
  { code: 'fa', name: 'Persian', nativeName: 'فارسی' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు' },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી' },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ' },
];

// Location-based language mapping
const getLocationBasedLanguages = (): string[] => {
  try {
    // Try to detect user's locale and timezone
    const userLocale = navigator.language || navigator.languages?.[0] || 'en';
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    console.log('🌍 Detected locale:', userLocale, 'timezone:', timezone);
    
    // Extract primary language code
    const primaryLang = userLocale.split('-')[0].toLowerCase();
    
    // Priority languages based on detected locale
    let priorityLanguages: string[] = [primaryLang];
    
    // Add related languages based on region/timezone
    if (timezone) {
      if (timezone.includes('Asia/Kolkata') || timezone.includes('Asia/Calcutta')) {
        priorityLanguages = ['hi', 'en', 'mr', 'ta', 'te', 'bn', 'gu', 'kn', 'ml', 'pa'];
      } else if (timezone.includes('Asia/Seoul')) {
        priorityLanguages = ['ko', 'en', 'ja', 'zh'];
      } else if (timezone.includes('Asia/Tokyo')) {
        priorityLanguages = ['ja', 'en', 'ko', 'zh'];
      } else if (timezone.includes('Asia/Shanghai') || timezone.includes('Asia/Hong_Kong')) {
        priorityLanguages = ['zh', 'en', 'ja', 'ko'];
      } else if (timezone.includes('Europe/')) {
        // European timezones
        if (timezone.includes('London')) {
          priorityLanguages = ['en', 'fr', 'de', 'es', 'it'];
        } else if (timezone.includes('Paris')) {
          priorityLanguages = ['fr', 'en', 'de', 'es', 'it'];
        } else if (timezone.includes('Berlin')) {
          priorityLanguages = ['de', 'en', 'fr', 'pl', 'nl'];
        } else if (timezone.includes('Madrid')) {
          priorityLanguages = ['es', 'en', 'fr', 'pt'];
        } else if (timezone.includes('Rome')) {
          priorityLanguages = ['it', 'en', 'fr', 'de', 'es'];
        } else {
          priorityLanguages = ['en', 'de', 'fr', 'es', 'it'];
        }
      } else if (timezone.includes('America/')) {
        // American timezones
        if (timezone.includes('New_York') || timezone.includes('Los_Angeles')) {
          priorityLanguages = ['en', 'es', 'fr'];
        } else if (timezone.includes('Mexico')) {
          priorityLanguages = ['es', 'en'];
        } else if (timezone.includes('Sao_Paulo')) {
          priorityLanguages = ['pt', 'es', 'en'];
        } else {
          priorityLanguages = ['en', 'es', 'fr'];
        }
      } else if (timezone.includes('Africa/')) {
        priorityLanguages = ['en', 'fr', 'ar', 'pt'];
      } else if (timezone.includes('Australia/')) {
        priorityLanguages = ['en'];
      }
    }
    
    // Ensure English is always included and primary language is first if not already
    if (!priorityLanguages.includes('en')) {
      priorityLanguages.push('en');
    }
    
    // Move detected primary language to front if it's in our supported languages
    if (primaryLang !== 'en' && AVAILABLE_LANGUAGES.some(lang => lang.code === primaryLang)) {
      priorityLanguages = [primaryLang, ...priorityLanguages.filter(lang => lang !== primaryLang)];
    }
    
    console.log('🌍 Priority languages based on location:', priorityLanguages);
    return priorityLanguages;
  } catch (error) {
    console.warn('Error detecting location for language ordering:', error);
    return ['en'];
  }
};

const LanguageSelectionStep: React.FC<LanguageSelectionStepProps> = ({
  initialLanguages,
  onComplete,
  onSkip,
  onBack,
}) => {
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(
    initialLanguages.length > 0 ? initialLanguages : ['en']
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [orderedLanguages, setOrderedLanguages] = useState<Language[]>(AVAILABLE_LANGUAGES);

  // Order languages based on user's location on component mount
  useEffect(() => {
    const priorityLanguageCodes = getLocationBasedLanguages();
    
    // Create ordered list: priority languages first, then rest alphabetically
    const priorityLanguages = priorityLanguageCodes
      .map(code => AVAILABLE_LANGUAGES.find(lang => lang.code === code))
      .filter((lang): lang is Language => lang !== undefined);
    
    const remainingLanguages = AVAILABLE_LANGUAGES
      .filter(lang => !priorityLanguageCodes.includes(lang.code))
      .sort((a, b) => a.name.localeCompare(b.name));
    
    setOrderedLanguages([...priorityLanguages, ...remainingLanguages]);
  }, []);

  // Filter languages based on search query
  const filteredLanguages = searchQuery 
    ? orderedLanguages.filter(language =>
        language.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        language.nativeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        language.code.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : orderedLanguages;

  const handleLanguageSelect = (languageCode: string) => {
    setSelectedLanguages(prev => {
      if (prev.includes(languageCode)) {
        // Don't allow deselecting if it's the only selected language
        if (prev.length === 1) return prev;
        return prev.filter(code => code !== languageCode);
      } else {
        return [...prev, languageCode];
      }
    });
  };

  const handleContinue = () => {
    // Save languages and complete onboarding (go to homepage)
    const languagesToSave = selectedLanguages.length > 0 ? selectedLanguages : ['en'];
    console.log('🎬 LanguageSelectionStep: Completing with languages:', languagesToSave);
    onComplete(languagesToSave);
    // Note: onComplete will handle marking onboarding as completed and navigating to homepage
    // We should NOT call onSkip here as it would mark onboarding as skipped instead of completed
  };

  const handleSkip = () => {
    if (onSkip) {
      // First update the parent with current language selection
      const languagesToSave = selectedLanguages.length > 0 ? selectedLanguages : ['en'];
      console.log('🎬 LanguageSelectionStep: Skipping with languages:', languagesToSave);
      onComplete(languagesToSave);
      // Then call skip after a brief delay to ensure languages are saved
      setTimeout(() => {
        onSkip(); // Skip entire onboarding flow
      }, 100);
    } else {
      onComplete(['en']); // Fallback to default behavior
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Progress calculation for app bar - Step 1 of 2
  const progressBars: JSX.Element[] = [];
  for (let i = 0; i < 2; i++) {
    const isActive = i === 0; // Step 1
    const isCompleted = false; // No steps completed yet
    progressBars.push(
      <Box
        key={i}
        sx={{
          width: '24px',
          height: '4px',
          borderRadius: '2px',
          bgcolor: isActive ? '#ff6b82' : 'rgba(255, 255, 255, 0.3)',
          transition: 'all 0.3s ease',
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Transparent App Bar with Back Button, Progress, and Skip - with safe area support */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pt: 'var(--safe-area-inset-top)',
          height: 'calc(var(--safe-area-inset-top) + 56px)',
          background: 'rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(10px)',
        }}
      >
        {/* Back Button */}
        {onBack ? (
          <IconButton
            onClick={onBack}
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <ArrowLeftIcon />
          </IconButton>
        ) : (
          <Box sx={{ width: '48px' }} />
        )}

        {/* Progress Bar in App Bar */}
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            justifyContent: 'center',
            flex: 1,
            mx: 2,
          }}
        >
          {progressBars}
        </Box>

        {/* Skip Button */}
        <CustomButton
          variant="text"
          onClick={handleSkip}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            fontFamily: font(),
            fontWeight: 500,
            fontSize: '14px',
            textTransform: 'none',
            minWidth: 'auto',
            px: 2,
            '&:hover': {
              color: 'rgba(255, 255, 255, 0.9)',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Skip
        </CustomButton>
      </Box>

      {/* Content Container */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          pt: 'calc(var(--safe-area-inset-top) + 80px)', // Space for app bar
          overflow: 'hidden', // Important for child scrolling
          height: '100vh', // Ensure proper height
        }}
      >
        {/* Title and Search Container */}
        <Box sx={{ px: { xs: 2, sm: 3, md: 0 } }}>
          <ResponsiveContainer>
            {/* Title - More Compact */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography
                sx={{
                  fontFamily: font(),
                  fontWeight: 600,
                  fontSize: '22px',
                  color: 'rgba(255, 255, 255, 0.95)',
                  lineHeight: 1.3,
                }}
              >
                Which languages do you watch movies in?
              </Typography>
            </Box>

            {/* Search Bar */}
            <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search languages..."
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearSearch}
                    size="small"
                    sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                  >
                    <Clear sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(0, 0, 0, 0.3)',
                borderRadius: '12px',
                height: '48px',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#7D7AFF',
                },
              },
              '& .MuiInputBase-input': {
                color: 'rgba(255, 255, 255, 0.95)',
                fontFamily: font(),
                fontSize: '14px',
                '&::placeholder': {
                  color: 'rgba(255, 255, 255, 0.6)',
                  opacity: 1,
                },
              },
            }}
          />
            </Box>
          </ResponsiveContainer>
        </Box>

        {/* Language Grid - More Compact with Scrolling */}
        <Box
          sx={{
            flex: 1,
            px: { xs: 2, sm: 3, md: 0 },
            pb: 16, // Reduced space for fixed buttons
            overflowY: 'auto', // Enable scrolling
            overflowX: 'hidden',
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '2px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '2px',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.5)',
              },
            },
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1)',
          }}
        >
          <ResponsiveContainer>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 2, // Reduced gap
                maxWidth: '100%',
                mx: 'auto',
              }}
            >
            {filteredLanguages.map((language, index) => {
              const isSelected = selectedLanguages.includes(language.code);
              
              return (
                <Fade
                  key={language.code}
                  in={true}
                  timeout={300}
                >
                  <Box
                    onClick={() => handleLanguageSelect(language.code)}
                    sx={{
                      bgcolor: isSelected
                        ? 'rgba(125, 122, 255, 0.2)'
                        : 'rgba(255, 255, 255, 0.1)',
                      border: isSelected
                        ? '2px solid #7D7AFF'
                        : '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px', // Slightly smaller
                      p: 2, // Reduced padding
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      transitionDelay: `${index * 30}ms`,
                      position: 'relative',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        transform: 'translateY(-1px)', // Reduced hover effect
                        bgcolor: isSelected 
                          ? 'rgba(125, 122, 255, 0.3)' 
                          : 'rgba(125, 122, 255, 0.3)',
                        borderColor: isSelected 
                          ? 'rgba(125, 122, 255, 1)' 
                          : 'rgba(125, 122, 255, 1)',
                      },
                    }}
                  >
                    {/* Language Code Badge */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: isSelected 
                          ? 'rgba(125, 122, 255, 1)' 
                          : 'rgba(255, 255, 255, 0.2)',
                        color: isSelected 
                          ? 'white' 
                          : 'rgba(255, 255, 255, 0.7)',
                        px: 1,
                        py: 0.25,
                        borderRadius: '6px',
                        fontSize: '9px',
                        fontWeight: 600,
                        fontFamily: font(),
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {language.code}
                    </Box>

                    {/* Language Name */}
                    <Typography
                      sx={{
                        fontFamily: font(),
                        fontWeight: 600,
                        fontSize: '14px', // Slightly smaller
                        color: 'rgba(255, 255, 255, 0.95)',
                        mb: 0.25,
                        pr: 4, // Space for badge
                      }}
                    >
                      {language.name}
                    </Typography>

                    {/* Native Name */}
                    <Typography
                      sx={{
                        fontFamily: font(),
                        fontWeight: 400,
                        fontSize: '12px', // Slightly smaller
                        color: 'rgba(255, 255, 255, 0.7)',
                        pr: 4, // Space for badge
                      }}
                    >
                      {language.nativeName}
                    </Typography>
                  </Box>
                </Fade>
              );
            })}
            </Box>
          </ResponsiveContainer>
        </Box>
      </Box>

      {/* Fixed Action Buttons - Only Continue Button */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          p: 3,
          pb: 'calc(env(safe-area-inset-bottom) + 24px)',
          background: 'linear-gradient(180deg, transparent 0%, rgba(26, 26, 46, 0.8) 20%, rgba(26, 26, 46, 1) 100%)',
          backdropFilter: 'blur(20px)',
          display: 'flex',
          gap: 2,
        }}
      >
        {/* Continue Button - Full Width */}
        <CustomButton
          variant="contained"
          semantic="brand"
          onClick={handleContinue}
          sx={{
            flex: 1,
            height: '56px',
            borderRadius: '12px',
            fontFamily: font(),
            fontWeight: 600,
            fontSize: '16px',
            textTransform: 'none',
          }}
        >
          Continue
        </CustomButton>
      </Box>
    </Box>
  );
};

export default LanguageSelectionStep; 